import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { money, pct, dateShort } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Plus, ShieldCheck } from "lucide-react";

const STATUSES = [
  ["verified", "Verified accurate", "text-[hsl(var(--success))]"],
  ["needs_review", "Needs review", "text-[hsl(var(--warning))]"],
  ["incorrect", "Incorrect", "text-[hsl(var(--destructive))]"],
  ["contract_issue", "Contract issue", "text-[hsl(var(--destructive))]"],
  ["accessorial_issue", "Accessorial issue", "text-[hsl(var(--warning))]"],
  ["fak_issue", "FAK / class issue", "text-[hsl(var(--warning))]"],
  ["api_credential_issue", "API credential issue", "text-[hsl(var(--destructive))]"],
];
const empty = { quote_id: "", carrier_code: "FEDEX_FREIGHT", dashboard_rate: 0, portal_rate: 0, invoice_rate: 0, manual_rate: 0, status: "needs_review", notes: "" };

export default function Validation() {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);

  const load = () => api.get("/validation").then((r) => setRows(r.data)).catch((e) => toast.error(apiError(e)));
  useEffect(() => { load(); }, []);

  const save = async () => {
    const payload = { ...form };
    ["dashboard_rate", "portal_rate", "invoice_rate", "manual_rate"].forEach((k) => (payload[k] = Number(payload[k])));
    try { await api.post("/validation", payload); toast.success("Validation recorded"); setOpen(false); setForm(empty); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const statusMeta = (s) => STATUSES.find((x) => x[0] === s) || STATUSES[1];

  return (
    <div className="space-y-5 animate-fade-up">
      <div className="flex items-end justify-between">
        <div>
          <h1 className="font-heading text-3xl font-black tracking-tighter">FedEx Rate Validation</h1>
          <p className="mt-1 text-sm text-muted-foreground">Prove rate accuracy against the portal & invoices before booking is ever enabled</p>
        </div>
        <Button onClick={() => { setForm(empty); setOpen(true); }} data-testid="add-validation-button" className="bg-[hsl(var(--primary))] text-white hover:bg-[hsl(var(--primary))]/90"><Plus className="mr-1.5 h-4 w-4" />New Check</Button>
      </div>

      <div className="border border-border bg-card">
        <Table>
          <TableHeader>
            <TableRow className="border-border hover:bg-transparent">
              <TableHead>Date</TableHead>
              <TableHead>Carrier</TableHead>
              <TableHead className="text-right">Dashboard</TableHead>
              <TableHead className="text-right">Portal</TableHead>
              <TableHead className="text-right">Invoice</TableHead>
              <TableHead className="text-right">Diff $</TableHead>
              <TableHead className="text-right">Diff %</TableHead>
              <TableHead>Pass/Fail</TableHead>
              <TableHead>Status</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.length === 0 && <TableRow><TableCell colSpan={9} className="py-10 text-center text-muted-foreground"><ShieldCheck className="mx-auto mb-2 h-7 w-7 opacity-40" />No validation checks yet.</TableCell></TableRow>}
            {rows.map((r) => {
              const sm = statusMeta(r.status);
              return (
                <TableRow key={r.id} className="border-border hover:bg-secondary/40" data-testid={`validation-row-${r.id}`}>
                  <TableCell className="text-xs text-muted-foreground">{dateShort(r.created_at)}</TableCell>
                  <TableCell className="text-sm">{r.carrier_code?.replace("_", " ")}</TableCell>
                  <TableCell className="metric-num text-right text-sm">{money(r.dashboard_rate)}</TableCell>
                  <TableCell className="metric-num text-right text-sm">{money(r.portal_rate)}</TableCell>
                  <TableCell className="metric-num text-right text-sm">{money(r.invoice_rate)}</TableCell>
                  <TableCell className="metric-num text-right text-sm">{money(r.difference_dollars)}</TableCell>
                  <TableCell className="metric-num text-right text-sm">{pct(r.difference_pct)}</TableCell>
                  <TableCell>
                    <span className={`text-xs font-bold uppercase ${r.pass_fail === "pass" ? "text-[hsl(var(--success))]" : r.pass_fail === "fail" ? "text-[hsl(var(--destructive))]" : "text-muted-foreground"}`}>{r.pass_fail}</span>
                  </TableCell>
                  <TableCell><span className={`text-xs font-semibold ${sm[2]}`}>{sm[1]}</span></TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="validation-dialog">
          <DialogHeader><DialogTitle className="font-heading">Record Rate Validation</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2"><Label className="label-caps">Quote Reference (optional)</Label><Input value={form.quote_id} onChange={(e) => setForm({ ...form, quote_id: e.target.value })} className="mt-1.5 h-10" /></div>
            <div><Label className="label-caps">Dashboard Rate ($)</Label><Input type="number" value={form.dashboard_rate} onChange={(e) => setForm({ ...form, dashboard_rate: e.target.value })} className="mt-1.5 h-10" data-testid="val-dashboard" /></div>
            <div><Label className="label-caps">Portal Rate ($)</Label><Input type="number" value={form.portal_rate} onChange={(e) => setForm({ ...form, portal_rate: e.target.value })} className="mt-1.5 h-10" data-testid="val-portal" /></div>
            <div><Label className="label-caps">Invoice Rate ($)</Label><Input type="number" value={form.invoice_rate} onChange={(e) => setForm({ ...form, invoice_rate: e.target.value })} className="mt-1.5 h-10" /></div>
            <div><Label className="label-caps">Manual Quote ($)</Label><Input type="number" value={form.manual_rate} onChange={(e) => setForm({ ...form, manual_rate: e.target.value })} className="mt-1.5 h-10" /></div>
            <div className="col-span-2">
              <Label className="label-caps">Status</Label>
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger className="mt-1.5 h-10" data-testid="val-status"><SelectValue /></SelectTrigger>
                <SelectContent>{STATUSES.map((s) => <SelectItem key={s[0]} value={s[0]}>{s[1]}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="col-span-2"><Label className="label-caps">Notes</Label><Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} className="mt-1.5" /></div>
          </div>
          <DialogFooter><Button onClick={save} data-testid="save-validation-button" className="bg-[hsl(var(--primary))] text-white hover:bg-[hsl(var(--primary))]/90">Save Check</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
